/**
 * Tiptap Suggestion implementation for context reference @ mentions.
 *
 * Renders a floating dropdown below the cursor with:
 * - Empty state: "Browse by type" categories + recent items
 * - Search state: flat ranked results from the API
 */

import { entityIconSvg } from "../shared/entity-icons";

// Prefix-to-type mapping for power-user shortcuts
const PREFIX_MAP: Record<string, string> = {
  project: "project",
  task: "task",
  wiki: "wiki_page",
  knowledge: "knowledge_source",
  conversation: "conversation",
  appointment: "appointment",
  user: "user",
  step: "workflow_step",
  property: "property",
};

// Default entity type display labels (overridden by i18n labels when available)
const DEFAULT_TYPE_LABELS: Record<string, string> = {
  project: "Projects",
  task: "Tasks",
  wiki_page: "Wiki Pages",
  knowledge_source: "Knowledge",
  conversation: "Conversations",
  appointment: "Appointments",
  user: "Users",
  workflow_step: "Steps",
  property: "Accommodations",
};

interface SearchResult {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  icon?: string;
}

interface SearchResponse {
  results: SearchResult[];
  counts?: Record<string, number>;
}

/**
 * Parse prefix syntax from query string.
 * e.g. "task:berlin" -> { type: "task", query: "berlin" }
 * e.g. "berlin" -> { type: undefined, query: "berlin" }
 */
function parseQuery(raw: string): { type?: string; query: string } {
  const colonIdx = raw.indexOf(":");
  if (colonIdx > 0) {
    const prefix = raw.slice(0, colonIdx).toLowerCase();
    if (PREFIX_MAP[prefix]) {
      return { type: PREFIX_MAP[prefix], query: raw.slice(colonIdx + 1) };
    }
  }
  return { query: raw };
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

async function fetchResults(query: string): Promise<SearchResponse> {
  const { type, query: q } = parseQuery(query);
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (type) params.set("type", type);

  try {
    const resp = await fetch(`/api/context-references/search?${params.toString()}`);
    if (!resp.ok) return { results: [] };
    return await resp.json();
  } catch {
    return { results: [] };
  }
}

function getIconForType(type: string): string {
  return entityIconSvg(type);
}

/**
 * Creates a Tiptap Suggestion config for the context reference @ mention.
 */
export function contextReferenceSuggestion(labels: Record<string, string> = {}) {
  return {
    char: "@",
    allowSpaces: false,
    items: async ({
      query,
    }: {
      query: string;
    }): Promise<Array<SearchResult | { _category: true; type: string; count: number }>> => {
      return new Promise((resolve) => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
          const data = await fetchResults(query);

          if (!query && data.counts) {
            // Empty state: categories + recent results
            const categories = Object.entries(data.counts)
              .filter(([, count]) => count > 0)
              .map(([type, count]) => ({ _category: true as const, type, count }));
            resolve([...categories, ...data.results]);
          } else {
            resolve(data.results);
          }
        }, 150);
      });
    },

    render: () => {
      let popup: HTMLElement | null = null;
      let selectedIndex = 0;
      let items: any[] = [];
      let command: ((props: any) => void) | null = null;
      let editorRef: any = null;
      let rangeRef: { from: number; to: number } | null = null;

      function createPopup(): HTMLElement {
        const el = document.createElement("div");
        el.className =
          "bg-base-100 border border-base-300 rounded-xl shadow-xl overflow-hidden z-[9999] max-h-[40vh] overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-100";
        el.style.position = "fixed";
        el.style.minWidth = "240px";
        el.style.maxWidth = "360px";
        document.body.appendChild(el);
        return el;
      }

      function renderItems() {
        if (!popup) return;
        const isMobile = window.innerWidth < 768;

        let html = "";
        let hasCategories = false;
        let hasResults = false;

        for (let i = 0; i < items.length; i++) {
          const item = items[i];

          if (item._category) {
            if (!hasCategories) {
              html += `<div class="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest opacity-40 font-bold">${labels.browse_by_type || "Browse by type"}</div>`;
              hasCategories = true;
            }
            const typeLabels: Record<string, string> = {
              project: labels.projects || DEFAULT_TYPE_LABELS.project,
              task: labels.tasks || DEFAULT_TYPE_LABELS.task,
              wiki_page: labels.wiki_pages || DEFAULT_TYPE_LABELS.wiki_page,
              knowledge_source: labels.knowledge || DEFAULT_TYPE_LABELS.knowledge_source,
              conversation: labels.conversations || DEFAULT_TYPE_LABELS.conversation,
              appointment: labels.appointments || DEFAULT_TYPE_LABELS.appointment,
              user: labels.users || DEFAULT_TYPE_LABELS.user,
              workflow_step: labels.steps || DEFAULT_TYPE_LABELS.workflow_step,
              property: labels.accommodations || DEFAULT_TYPE_LABELS.property,
            };
            const isSelected = i === selectedIndex;
            html += `<button data-index="${i}" data-active="${isSelected}" class="ctx-ref-item w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors">
                            <span class="opacity-60">${entityIconSvg(item.type)}</span>
                            <span class="font-medium">${typeLabels[item.type] || item.type}</span>
                            <span class="text-[11px] opacity-40">(${item.count})</span>
                            ${!isMobile ? `<span class="ml-auto text-[10px] opacity-30">${labels.tab_to_filter || "tab to filter"}</span>` : ""}
                        </button>`;
          } else {
            if (hasCategories && !hasResults) {
              html += `<div class="border-t border-base-300/50 mx-2 my-1"></div>`;
              html += `<div class="px-3 pt-1 pb-1 text-[10px] uppercase tracking-widest opacity-40 font-bold">${labels.recent || "Recent"}</div>`;
              hasResults = true;
            }
            const isSelected = i === selectedIndex;
            const icon = getIconForType(item.type);
            html += `<button data-index="${i}" data-active="${isSelected}" class="ctx-ref-item w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors">
                            <span class="opacity-60 flex-shrink-0">${icon}</span>
                            <span class="font-medium truncate">${escapeHtml(item.title)}</span>
                            ${!isMobile && item.subtitle ? `<span class="text-[11px] opacity-40 truncate ml-auto">${escapeHtml(item.subtitle)}</span>` : ""}
                        </button>`;
          }
        }

        if (items.length === 0) {
          html = `<div class="px-3 py-4 text-sm opacity-40 text-center">${labels.no_results || "No results"}</div>`;
        }

        popup.innerHTML = html;

        // Add click handlers
        popup.querySelectorAll("button[data-index]").forEach((btn) => {
          btn.addEventListener("mousedown", (e) => {
            e.preventDefault();
            const idx = parseInt((btn as HTMLElement).dataset.index || "0");
            selectItem(idx);
          });
        });
      }

      function getAccentColor(): { bg: string; fg: string } {
        // Resolve the accent color from the themed context (not document.body)
        const themed = document.querySelector("[data-theme]") || document.documentElement;
        const style = getComputedStyle(themed);
        const accent = style.getPropertyValue("--a").trim();
        if (accent) {
          return {
            bg: `oklch(${accent} / 0.12)`,
            fg: `oklch(${accent})`,
          };
        }
        // Fallback
        return { bg: "rgba(200, 150, 80, 0.12)", fg: "rgb(200, 150, 80)" };
      }

      function applySelection() {
        if (!popup) return;
        const { bg, fg } = getAccentColor();
        const buttons = popup.querySelectorAll("button[data-index]");
        buttons.forEach((btn) => {
          const el = btn as HTMLElement;
          const idx = parseInt(el.dataset.index || "-1");
          if (idx === selectedIndex) {
            el.style.background = bg;
            el.style.color = fg;
            el.scrollIntoView({ block: "nearest" });
          } else {
            el.style.background = "";
            el.style.color = "";
          }
        });
      }

      function selectItem(index: number) {
        const item = items[index];
        if (!item || !command) return;

        if (item._category) {
          // Type the prefix into the editor so the suggestion re-triggers
          // e.g. selecting "Tasks" types "task:" after the @
          const prefix = Object.entries(PREFIX_MAP).find(([, v]) => v === item.type)?.[0];
          if (prefix && editorRef && rangeRef) {
            // Replace everything after @ with the prefix, keeping the @
            const afterAt = rangeRef.from + 1; // skip the @ character
            const to = rangeRef.to;
            editorRef
              .chain()
              .focus()
              .deleteRange({ from: afterAt, to })
              .insertContentAt(afterAt, prefix + ":")
              .run();
          }
          return;
        }

        command({
          id: `${item.type}:${item.id}`,
          label: item.title,
          type: item.type,
        });
      }

      function positionPopup(clientRect: (() => DOMRect | null) | null) {
        if (!popup || !clientRect) return;
        const rect = typeof clientRect === "function" ? clientRect() : clientRect;
        if (!rect) return;

        // Always open upward — chat input is at the bottom of the page
        // Position above the cursor initially, adjust after measuring
        popup.style.left = `${rect.left}px`;
        popup.style.top = "auto";
        popup.style.bottom = `${window.innerHeight - rect.top + 4}px`;

        // Ensure popup doesn't go off-screen horizontally
        requestAnimationFrame(() => {
          if (!popup) return;
          const popupRect = popup.getBoundingClientRect();
          if (popupRect.right > window.innerWidth - 8) {
            popup.style.left = `${window.innerWidth - popupRect.width - 8}px`;
          }
          if (popupRect.left < 8) {
            popup.style.left = "8px";
          }
        });
      }

      return {
        onStart: (props: any) => {
          command = props.command;
          editorRef = props.editor;
          rangeRef = props.range;
          items = props.items || [];
          selectedIndex = 0;
          popup = createPopup();
          positionPopup(props.clientRect);
          renderItems();
          applySelection();
        },

        onUpdate: (props: any) => {
          command = props.command;
          editorRef = props.editor;
          rangeRef = props.range;
          items = props.items || [];
          selectedIndex = 0;
          positionPopup(props.clientRect);
          renderItems();
          applySelection();
        },

        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
          if (items.length === 0) return false;

          if (event.key === "ArrowDown") {
            selectedIndex = (selectedIndex + 1) % items.length;
            applySelection();
            return true;
          }
          if (event.key === "ArrowUp") {
            selectedIndex = (selectedIndex - 1 + items.length) % items.length;
            applySelection();
            return true;
          }
          if (event.key === "Enter" || event.key === "Tab") {
            selectItem(selectedIndex);
            return true;
          }
          if (event.key === "Escape") {
            return true;
          }
          return false;
        },

        onExit: () => {
          if (popup) {
            popup.remove();
            popup = null;
          }
          command = null;
        },
      };
    },
  };
}

function escapeHtml(str: string): string {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}
